import { kebabCase, mapKeys } from 'lodash/fp';
import {
  getElementPlaceholder,
  getHostedFieldSelector,
} from '@zapier/payment/src/components/CreditCardForm/HostedFields/utils';
import {
  CreditCardBrandCode,
  HostedFieldAdditionalData,
  HostedFieldProcessorInterface,
  HostedFieldToken,
  HostedFieldType,
  HostedFieldValue,
} from '@zapier/payment/src/types';

const braintreeToType = {
  number: 'number',
  expirationDate: 'expiry',
  cvv: 'cvc',
} as const;

// This type is likely wrong and needs to be fleshed out and extracted to types.ts
type BraintreeEvent = {
  emittedBy: keyof typeof braintreeToType;
  fields: {
    [key in keyof typeof braintreeToType]: {
      isEmpty: boolean;
      isValid: boolean;
      isPotentiallyValid: boolean;
    };
  };
  cards: Array<{ type: CreditCardBrandCode }>;
};

const getCardType = (event: BraintreeEvent) => {
  return event.cards.length === 1 ? event.cards[0].type : null;
};

class BraintreeProcessor implements HostedFieldProcessorInterface {
  client: any = null;
  hostFields: any = null;
  static Client: any = null;
  static HostFields: any = null;
  static instance: BraintreeProcessor | undefined | null = null;

  constructor(client: any) {
    this.client = client;
  }

  async createFields(
    style: any,
    onChange: (b: HostedFieldType, a: HostedFieldValue) => void
  ) {
    this.hostFields = await BraintreeProcessor.HostFields.create({
      client: this.client,
      styles: {
        input: mapKeys(kebabCase, style),
        '::placeholder': mapKeys(kebabCase, style['::placeholder']),
      },
      fields: {
        number: {
          selector: getHostedFieldSelector('number'),
          placeholder: getElementPlaceholder('number'),
        },
        expirationDate: {
          selector: getHostedFieldSelector('expiry'),
          placeholder: getElementPlaceholder('expiry'),
        },
        cvv: {
          selector: getHostedFieldSelector('cvc'),
          placeholder: getElementPlaceholder('cvc'),
        },
      },
    }); // TODO(ngryman): refactor this!

    this.hostFields.on('cardTypeChange', (event: BraintreeEvent) => {
      const field = event.fields[event.emittedBy];
      const type = braintreeToType[event.emittedBy];
      onChange(type, {
        brand: getCardType(event),
        isEmpty: field.isEmpty,
        originalError: '',
      });
    });

    this.hostFields.on('empty', (event: BraintreeEvent) => {
      const type = braintreeToType[event.emittedBy];
      onChange(type, {
        brand: getCardType(event),
        isEmpty: true,
        originalError: '',
      });
    });

    this.hostFields.on('noEmpty', (event: BraintreeEvent) => {
      const type = braintreeToType[event.emittedBy];
      onChange(type, {
        brand: getCardType(event),
        isEmpty: false,
        originalError: '',
      });
    });

    this.hostFields.on('validityChange', (event: BraintreeEvent) => {
      const field = event.fields[event.emittedBy];
      const type = braintreeToType[event.emittedBy];
      onChange(type, {
        brand: getCardType(event),
        isEmpty: field.isEmpty,
        originalError:
          !field.isValid && !field.isPotentiallyValid
            ? `Your card ${type} is invalid.`
            : '',
      });
    });

    this.hostFields.on('blur', (event: BraintreeEvent) => {
      const field = event.fields[event.emittedBy];
      const type = braintreeToType[event.emittedBy];
      onChange(type, {
        brand: getCardType(event),
        isEmpty: field.isEmpty,
        originalError: !field.isValid ? `Your card ${type} is invalid.` : '',
      });
    });
  }

  destroyFields() {
    if (this.hostFields) {
      this.hostFields.teardown();
    }
  }

  async createToken(
    additionalData: HostedFieldAdditionalData
  ): Promise<HostedFieldToken> {
    const token = await this.hostFields.tokenize({
      cardholderName: additionalData.name,
      billingAddress: {
        countryCodeAlpha2: additionalData.addressCountry,
        postalCode: additionalData.addressZip,
        region: additionalData.addressState,
      },
    });

    return {
      token: token.nonce,
      details: {
        brand: token.details.cardType,
        exp_month: '',
        exp_year: '',
        last4: token.details.lastFour,
        address_state: additionalData.addressState,
        address_city: additionalData.addressCity,
        address_zip: additionalData.addressZip,
        address_country: additionalData.addressCountry,
      },
    };
  }

  static async create(apiKey: string): Promise<HostedFieldProcessorInterface> {
    if (!BraintreeProcessor.instance) {
      if (!BraintreeProcessor.Client) {
        const { client, hostedFields } = await import('braintree-web');
        BraintreeProcessor.Client = client;
        BraintreeProcessor.HostFields = hostedFields;
      }

      const client = await BraintreeProcessor.Client.create({
        authorization: apiKey,
      });

      BraintreeProcessor.instance = new BraintreeProcessor(client);
    }

    return BraintreeProcessor.instance;
  }
}

export default BraintreeProcessor.create;
